import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from 'react';

/** Never smaller than the frame it sits in: zooming out past that shows nothing. */
const MIN_SCALE = 1;
const DEFAULT_MAX_SCALE = 6;
/** What one press of a zoom control is worth. */
const STEP = 1.6;
/** Movement past this, in px, is a drag rather than a tap on what is underneath. */
const DRAG_THRESHOLD = 6;
/** How close to the floor a scale still counts as resting there — rounding, not a gesture. */
const REST_TOLERANCE = 1e-3;

/**
 * How a control drawn over the frame says it is not part of the content: it sits
 * on top of the picture rather than on it, so a gesture that moved the picture
 * has no claim on its click. See onClickCapture.
 */
export const OVERLAY_ATTRIBUTE = 'data-zoom-overlay';

interface View {
  scale: number;
  x: number;
  y: number;
}

const IDENTITY: View = { scale: 1, x: 0, y: 0 };

const sameView = (a: View, b: View) => a.scale === b.scale && a.x === b.x && a.y === b.y;

/**
 * How big the content is laid out against the frame before any zoom: its width
 * over the frame's, and its height over the frame's.
 *
 * 1 × 1 wherever the picture sizes the frame, which is everywhere but a frame
 * given a height of its own — a landscape photo opened out to the height of the
 * screen, where the content is as wide as the frame and shorter than it.
 */
interface ContentRatio {
  x: number;
  y: number;
}

const SAME_SIZE: ContentRatio = { x: 1, y: 1 };

/**
 * The least the content may be scaled: enough to **cover** the frame. 1 when the
 * two are the same size; in a frame taller than the picture, whatever fills its
 * height — the picture then overflows sideways and is panned across instead.
 */
function coverScale(ratio: ContentRatio): number {
  return Math.max(MIN_SCALE, 1 / ratio.x, 1 / ratio.y);
}

export interface ZoomPan {
  /** Spread on the element that frames the content and clips it. */
  frameProps: {
    /**
     * A callback ref, not an object one: the wheel is a native non-passive
     * listener (React's own is passive, so it could not preventDefault), and it
     * has to be attached when the frame actually arrives. An effect reading a
     * ref cannot know that it has — a hook mounted before the element it frames,
     * which is what happens whenever the page renders a skeleton first, would
     * run its effect against `null` and never run it again.
     */
    ref: (node: HTMLDivElement | null) => void;
    style: CSSProperties;
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: PointerEvent<HTMLDivElement>) => void;
    onClickCapture: (event: MouseEvent<HTMLDivElement>) => void;
  };
  /** Spread on the element inside it that actually moves. */
  layerProps: { style: CSSProperties };
  /**
   * The moving element itself, for a frame that may be given a height of its
   * own instead of taking the picture's. Attached, the hook knows how big the
   * content is laid out against the frame: it never lets it cover less than the
   * frame (it rests filling it, centred), pans what spills over, and puts the
   * view back at rest whenever either box is resized under it.
   *
   * Left off, the content is taken to be exactly the frame's size — true of
   * every frame its picture sizes. A ref of its own rather than part of
   * `layerProps`, because some layers already carry a ref of their own.
   */
  contentRef: (node: HTMLElement | null) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Back to the view at rest — the whole picture, or the picture just covering a taller frame. */
  reset: () => void;
  /** Whether the content is larger than its frame, so there is something to pan. */
  zoomed: boolean;
  /**
   * Whether the view is the one it rests at: not scaled past what covers the
   * frame. The same as `!zoomed` on a frame its picture sizes; in a taller one
   * the picture already overflows sideways at rest, so it is panned (`zoomed`)
   * without having been zoomed into — which is what a way back out, or the
   * question of whether the framing is a crop, are asked against.
   */
  atRest: boolean;
  canZoomIn: boolean;
  /**
   * How much larger. Only wanted by a caller drawing something on the content
   * that is *not* part of it — a drag handle, whose job is to be grabbed and
   * which would otherwise swell into a blob covering the hold it is being
   * aimed at. Divide by this to keep it the size it looks on screen.
   */
  scale: number;
  /**
   * Where a point on the screen lands on the content, in fractions (0-1) of the
   * content's own box — the space marks on a photo are stored in. Undoes the pan
   * and the zoom, so a circle is drawn under the finger however far into the
   * picture it is. Null before the frame has been laid out.
   *
   * Not clamped: a press can land outside the content while it is zoomed, and
   * whoever is placing something has its own view on what to do about that.
   */
  toContent: (clientX: number, clientY: number) => { x: number; y: number } | null;
  /**
   * Where the content currently sits in the frame: how much it is scaled and how
   * far it has been dragged, in the frame's own pixels.
   *
   * The other way round from `toContent`, and wanted by anything drawn *beside*
   * the content rather than in it — a panel anchored to a mark has to follow the
   * picture as it is panned and zoomed without being scaled along with it, which
   * is what would happen inside the transformed layer.
   */
  view: View;
  /** The frame element, for a caller that measures it or captures pointers on it. */
  frameRef: RefObject<HTMLDivElement | null>;
  /**
   * The same element as state, for a caller that has to attach a **native**
   * listener to it — a non-passive one, which React cannot give. The ref alone
   * is no good for that: an effect reading it cannot know when the element
   * arrives, and a hook that mounted before its frame would bind to nothing.
   */
  frame: HTMLDivElement | null;
  /**
   * Give up the pan this gesture had armed, because the press turned out to
   * belong to something *on* the content — a circle being dragged, a vertex
   * being moved. Called by the surface that owns those marks, which is the only
   * thing that knows what the press landed on.
   */
  cancelPan: () => void;
}

export interface ZoomPanOptions {
  maxScale?: number;
}

/**
 * Pinching, dragging and wheeling a piece of content around inside a frame.
 *
 * A hook rather than a wrapper component because the controls belong wherever
 * the page wants them — on the board they sit in the legend beside the holds,
 * not floating over the photo.
 *
 * Applied to a board it scales the picture and the holds drawn on it as one
 * thing: the holds are placed in percentages of the same box, so a single
 * transform moves both and they cannot drift apart. That is the point of
 * transforming a container rather than the image — a screw-on foot chip is
 * about three pixels across on a phone, too small to hit, and enlarging only
 * the picture would leave the targets where they were.
 *
 * A tap still reaches what is underneath: only a gesture that actually travels
 * counts as a drag, and only then does the frame take the pointer and swallow
 * the click that follows.
 *
 * Two fingers always mean the picture itself: the distance between them scales
 * it and their centre drags it, in one gesture. One finger drags it too, once
 * there is more picture than frame — including on a surface being drawn on,
 * where the alternative is a gesture that everyone tries and nothing answers.
 * Such a surface hit-tests the press first and calls `cancelPan` when it landed
 * on a mark, which is the one case where dragging means something else.
 *
 * On a desktop, where there are no fingers to pinch or drag with, the wheel
 * carries the same gestures: ⌘ (or ctrl) and scroll to zoom about the cursor,
 * and, once the board is bigger than its frame, plain scroll to move around it
 * with shift for sideways. It gives the wheel back to the page at the edge it
 * is being scrolled towards, so a board can always be scrolled past.
 *
 * The frame is normally exactly the picture's box. It may also be given a height
 * of its own — a landscape photo opened out to the height of a phone's screen —
 * and then the picture **covers** it: it rests scaled up to fill the height, the
 * sides spill over and are panned across, and nothing zooms out past that. The
 * marks are still fractions of the picture, which is still the moving layer's
 * box, so nothing drawn on it needs to know (see `contentRef`).
 */
export function useZoomPan(options: ZoomPanOptions = {}): ZoomPan {
  const { maxScale = DEFAULT_MAX_SCALE } = options;
  const frameRef = useRef<HTMLDivElement | null>(null);
  // The frame as *state* as well as a ref, so the wheel listener is attached the
  // moment the element exists rather than only if it happened to exist when this
  // hook first ran.
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const attachFrame = useCallback((node: HTMLDivElement | null) => {
    frameRef.current = node;
    setFrame(node);
  }, []);
  const [view, setView] = useState<View>(IDENTITY);

  // The moving layer, when the caller hands it over (see `contentRef`) — as a
  // ref for the arithmetic and as state so it is observed once it arrives.
  const contentElement = useRef<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLElement | null>(null);
  const attachContent = useCallback((node: HTMLElement | null) => {
    contentElement.current = node;
    setContent(node);
  }, []);
  // The same ratio as state, for what is decided while rendering.
  const [ratio, setRatio] = useState<ContentRatio>(SAME_SIZE);

  /**
   * The content against the frame, read off the DOM as it is right now.
   *
   * Layout sizes on both sides, which no transform touches: the frame's inner
   * box (a bordered frame's layer sits inside the border) and the layer's own.
   * Nothing laid out yet — jsdom, a picture still loading — reads as the same
   * size, which is what every frame sized by its picture is anyway.
   */
  const measureRatio = useCallback((): ContentRatio => {
    const frameNode = frameRef.current;
    const contentNode = contentElement.current;
    if (!frameNode || !contentNode) return SAME_SIZE;

    const { clientWidth, clientHeight } = frameNode;
    const { offsetWidth, offsetHeight } = contentNode;
    if (!clientWidth || !clientHeight || !offsetWidth || !offsetHeight) return SAME_SIZE;

    return { x: offsetWidth / clientWidth, y: offsetHeight / clientHeight };
  }, []);

  const floor = coverScale(ratio);
  const zoomed = view.scale * ratio.x > 1 || view.scale * ratio.y > 1;
  const atRest = view.scale <= floor * (1 + REST_TOLERANCE);

  // The view as the wheel listener sees it. It is a native listener bound once
  // per zoom level, so it cannot read `view` out of the closure it was made in.
  const viewRef = useRef(view);
  viewRef.current = view;

  // Live pointers, so two of them can be told from one without React
  // re-rendering between the two events that make up a pinch.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchDistance = useRef<number | null>(null);
  // Where the two fingers were centred last, so the pair can drag the content
  // about as well as scale it — one gesture, as it feels under the hand.
  const pinchMidpoint = useRef<{ x: number; y: number } | null>(null);
  const panFrom = useRef<{ x: number; y: number; view: View } | null>(null);
  const dragged = useRef(false);
  // How much of this drag the picture has refused vertically, all told. Kept so
  // the page is scrolled by what is *new* on each move rather than by the whole
  // overflow again — and so dragging back off the edge unwinds it. See the pan.
  const panSpill = useRef(0);

  const contain = useCallback(
    (next: View): View => {
      const rect = frameRef.current?.getBoundingClientRect();
      const size = measureRatio();
      const floor = coverScale(size);
      const scale = Math.min(Math.max(next.scale, floor), floor * maxScale);
      if (!rect) return { ...IDENTITY, scale };

      // At scale 1 both bounds collapse to 0, which parks the content back in
      // the frame on its own — no special case for "zoomed all the way out".
      // A picture covering a taller frame keeps only the sideways bound open.
      return {
        scale,
        x: Math.min(0, Math.max(rect.width * (1 - size.x * scale), next.x)),
        y: Math.min(0, Math.max(rect.height * (1 - size.y * scale), next.y)),
      };
    },
    [maxScale, measureRatio]
  );

  /**
   * The view at rest: as small as the content may go, centred on whatever of it
   * spills over. The identity on a frame its picture sizes; on a landscape photo
   * opened out to a taller frame, its height filling the frame and the middle of
   * the wall on screen.
   */
  const restView = useCallback((): View => {
    const rect = frameRef.current?.getBoundingClientRect();
    const size = measureRatio();
    const scale = coverScale(size);
    if (!rect) return { ...IDENTITY, scale };

    return {
      scale,
      x: (rect.width * (1 - size.x * scale)) / 2,
      y: (rect.height * (1 - size.y * scale)) / 2,
    };
  }, [measureRatio]);

  /**
   * Scales about a point of the frame, so whatever is under the fingers (or the
   * cursor) stays under them. Takes a factor rather than a scale because a pinch
   * reports how much it changed, and reading the current scale out of state here
   * would use the value from before the previous move.
   */
  const zoomBy = useCallback(
    (factor: number, atX: number, atY: number, travel: { x: number; y: number } = { x: 0, y: 0 }) => {
      setView((current) => {
        const floor = coverScale(measureRatio());
        const scale = Math.min(Math.max(current.scale * factor, floor), floor * maxScale);
        const ratio = scale / current.scale;

        return contain({
          scale,
          x: atX - (atX - current.x) * ratio + travel.x,
          y: atY - (atY - current.y) * ratio + travel.y,
        });
      });
    },
    [contain, maxScale, measureRatio]
  );

  const zoomAboutCentre = useCallback(
    (factor: number) => {
      const rect = frameRef.current?.getBoundingClientRect();
      zoomBy(factor, (rect?.width ?? 0) / 2, (rect?.height ?? 0) / 2);
    },
    [zoomBy]
  );

  const spanOfPointers = () => {
    const [first, second] = [...pointers.current.values()];
    return {
      distance: Math.hypot(first.x - second.x, first.y - second.y),
      midpoint: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
    };
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // A fresh gesture: whatever the last one did, this one has not moved yet.
    if (pointers.current.size === 0) dragged.current = false;

    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2) {
      // A pinch is never a tap, so the frame can take both pointers now.
      pointers.current.forEach((_, id) => event.currentTarget.setPointerCapture(id));
      const span = spanOfPointers();
      pinchDistance.current = span.distance;
      pinchMidpoint.current = span.midpoint;
      panFrom.current = null;
      return;
    }

    // Panning only means something once there is more content than frame. It is
    // armed for every press, including on a surface being drawn on: there a
    // press may turn out to have grabbed a mark instead, and that surface says
    // so by calling cancelPan — which it can, because it is the only thing that
    // knows what is under the finger.
    if (zoomed) {
      panFrom.current = { x: event.clientX, y: event.clientY, view };
      panSpill.current = 0;
    }
  };

  const cancelPan = useCallback(() => {
    panFrom.current = null;
    panSpill.current = 0;
  }, []);

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size >= 2 && pinchDistance.current) {
      const { distance, midpoint } = spanOfPointers();
      if (distance <= 0) return;

      const rect = frameRef.current?.getBoundingClientRect();
      dragged.current = true;
      // The pair does both things at once: how far apart the fingers moved
      // scales the content, and where their centre travelled drags it. Applied
      // in that order and about the *new* centre, so whatever was between the
      // fingers when the gesture started stays between them.
      const from = pinchMidpoint.current ?? midpoint;
      zoomBy(
        distance / pinchDistance.current,
        midpoint.x - (rect?.left ?? 0),
        midpoint.y - (rect?.top ?? 0),
        { x: midpoint.x - from.x, y: midpoint.y - from.y }
      );
      pinchDistance.current = distance;
      pinchMidpoint.current = midpoint;
      return;
    }

    const from = panFrom.current;
    if (!from) return;

    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;

    // The frame takes the pointer the moment the gesture becomes a drag, and
    // not one event sooner. Capturing it on the way down — which is where it
    // used to happen — retargets the click that ends the gesture onto the
    // frame, so the hold under the cursor never hears about it and nothing gets
    // painted. Six pixels in, there is no tap left to protect, and capturing
    // here is what keeps a drag following the cursor off the edge of the frame.
    if (!dragged.current && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      dragged.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    const wanted = { ...from.view, x: from.view.x + dx, y: from.view.y + dy };
    const next = contain(wanted);
    setView(next);

    // Off the top or the bottom of the picture, the rest of the drag goes to the
    // page — the same bargain the wheel strikes at the edge (see the wheel
    // listener), so a reader who drags onto a zoomed picture can always drag
    // past it and is not held there.
    //
    // By hand, because zoomed in `touch-action` is `none`: the browser has
    // handed the whole gesture over and cannot be asked to take part of it back
    // half way through. What is applied is the *change* in what the picture
    // refused, so a finger held still scrolls nothing and one dragged back off
    // the edge gives the page back what it took.
    const spill = wanted.y - next.y;
    const step = spill - panSpill.current;
    panSpill.current = spill;
    if (step !== 0) window.scrollBy(0, -step);
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) {
      pinchDistance.current = null;
      pinchMidpoint.current = null;
    }
    if (pointers.current.size === 0) panFrom.current = null;
  };

  // A drag that ends over a hold would otherwise paint it on the way past.
  //
  // Only what the drag actually passed over: a control drawn *over* the frame —
  // the button that puts the zoom back — is not part of the picture being
  // dragged, and swallowing its click would leave the first press after every
  // gesture doing nothing. Such controls say so with `data-zoom-overlay`.
  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!dragged.current) return;
    if ((event.target as HTMLElement).closest?.(`[${OVERLAY_ATTRIBUTE}]`)) return;

    event.stopPropagation();
    event.preventDefault();
    dragged.current = false;
  };

  // Stable, so that putting the view back can be an effect's whole business —
  // a caller whose content changes under the frame (the next climb, swiped to)
  // depends on this without re-running on every render of its own.
  const reset = useCallback(() => setView(restView()), [restView]);

  const toContent = useCallback(
    (clientX: number, clientY: number) => {
      const rect = frameRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return null;

      // The moving layer is the content's own box transformed — the frame's,
      // unless the frame was given a height of its own — so undoing the
      // translate and the scale lands back in it, then out of pixels and into
      // the fractions every mark on a photo is stored in.
      const size = measureRatio();
      const { x, y, scale } = viewRef.current;
      return {
        x: (clientX - rect.left - x) / (rect.width * size.x * scale),
        y: (clientY - rect.top - y) / (rect.height * size.y * scale),
      };
    },
    [measureRatio]
  );

  // Where the frame is given a height of its own, what "the whole picture" is
  // moves whenever either box is resized: the photo finishing loading, the frame
  // opened out or folded back, a phone's toolbar coming and going under `dvh`.
  // A view at rest stays at rest across that — the floor moved, so it moves with
  // it — and one zoomed in is only kept inside the new bounds.
  const floorRef = useRef(MIN_SCALE);
  useEffect(() => {
    if (!frame || !content || typeof ResizeObserver === 'undefined') return;

    const refit = () => {
      const next = measureRatio();
      const wasResting = floorRef.current;
      floorRef.current = coverScale(next);

      setRatio((current) => (current.x === next.x && current.y === next.y ? current : next));
      setView((current) => {
        const fitted =
          current.scale <= wasResting * (1 + REST_TOLERANCE) ? restView() : contain(current);
        return sameView(fitted, current) ? current : fitted;
      });
    };

    const observer = new ResizeObserver(refit);
    observer.observe(frame);
    observer.observe(content);
    return () => observer.disconnect();
  }, [frame, content, measureRatio, restView, contain]);

  // What the wheel means over the board. React's own wheel handler is passive,
  // so preventDefault there would be ignored and the page would take the
  // gesture instead — hence the native listener.
  useEffect(() => {
    if (!frame) return;

    const onWheel = (event: WheelEvent) => {
      // Zoom. A trackpad pinch arrives as a wheel with ctrl held; ⌘ (and ctrl
      // on a mouse) is the same thing asked for deliberately, which is how a
      // wall gets read close up without a trackpad to pinch on.
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const rect = frame.getBoundingClientRect();
        zoomBy(Math.exp(-event.deltaY / 180), event.clientX - rect.left, event.clientY - rect.top);
        return;
      }

      // Below that there is no board outside the frame to bring into it, so the
      // page keeps the wheel — which is what anybody reading down the form under
      // the board is using it for.
      if (!zoomed) return;

      // Shift turns the wheel sideways, as it does everywhere else. Some
      // platforms have already turned it by the time it arrives and report it
      // on deltaX; the rest leave it on deltaY.
      const [dx, dy] = event.shiftKey
        ? [event.deltaX || event.deltaY, 0]
        : [event.deltaX, event.deltaY];

      const current = viewRef.current;
      const next = contain({ ...current, x: current.x - dx, y: current.y - dy });

      // Against the edge it is being scrolled towards, the board has nothing
      // left to show and hands the wheel back rather than swallowing it: a
      // reader who scrolls onto a zoomed board must still be able to scroll off
      // the other side of it, or the page is a trap.
      if (next.x === current.x && next.y === current.y) return;

      event.preventDefault();
      setView(next);
    };

    frame.addEventListener('wheel', onWheel, { passive: false });
    return () => frame.removeEventListener('wheel', onWheel);
  }, [frame, zoomBy, contain, zoomed]);

  return {
    frameRef,
    frame,
    frameProps: {
      ref: attachFrame,
      // Zoomed in, every gesture on the content is this frame's — there is more
      // picture than frame and dragging is how the rest of it is reached. At
      // rest only pinching is: a finger dragged up the page should still scroll
      // it, which is most of what anyone does on any of these screens, and is
      // true whether the picture is being read or drawn on.
      style: { touchAction: zoomed ? 'none' : 'pan-y' },
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onClickCapture,
    },
    layerProps: {
      style: {
        transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
        transformOrigin: '0 0',
        willChange: 'transform',
      },
    },
    contentRef: attachContent,
    zoomIn: () => zoomAboutCentre(STEP),
    zoomOut: () => zoomAboutCentre(1 / STEP),
    reset,
    zoomed,
    atRest,
    canZoomIn: view.scale < floor * maxScale,
    scale: view.scale,
    view,
    toContent,
    cancelPan,
  };
}
